<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from "vue";
import { storeToRefs } from "pinia";

import PageLayout from '@/components/layout/PageLayout.vue';
import routeManager from "@/router/route-manager";
import { useAppBreadcrumbs } from "@/composables/useAppBreadcrumbs";

import { useProductsStore } from "@/stores/Products";
import { useCoursesStore } from "@/stores/Courses";
import { Course, Product, Section } from "@tmi/types/entities";
import { useApplicationStore } from "@/stores/Application";

const productsStore = useProductsStore();
const coursesStore = useCoursesStore();
const { isLoading } = storeToRefs( coursesStore );

const { allCourses } = storeToRefs( coursesStore );

function productIcon( course: Course ): string {
  return primaryProductForCourse( course )?.favicon ?? "";
}

function productName( course: Course ): string {
  return primaryProductForCourse( course )?.name ?? "---";
}

function primaryProductForCourse( course: Course ): Product | undefined {
  return productsStore.productById( course.assoc_product_ids[ 0 ] );
}

function totalLessons( course: Course ) {
  let lessonsCounter = 0;
  course.sections.forEach( ( section: Section ) => lessonsCounter += section.lessons.length );
  return `${ lessonsCounter } lesson${ lessonsCounter > 1 ? 's' : '' }`;
}

const appStore = useApplicationStore();

const { trail, labelService } = useAppBreadcrumbs();
const breadcrumbItems = computed( () => trail( { label: labelService.label( 'lblCourses' ) } ) );

const show = ref< boolean >( false );
onMounted( async () => {
  await appStore.initializeAsync();
  show.value = true;
} );

onUnmounted( () => show.value = false );
</script>

<template>
  <transition mode="out-in" name="slide-down">
    <div v-if="show">
      <PageLayout :breadcrumb-items="breadcrumbItems" compact-header :content-card="false">
        <progress-spinner v-if="isLoading"/>
        <div v-else>
          <ul role="list" class="grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-2 lg:grid-cols-3 xl:gap-x-8">
            <li v-for="course in allCourses" :key="course.id" class="overflow-hidden card p-0">
              <div class="grow flex items-center gap-x-4 border-b border-gray-900/5 bg-neutral-50 dark:bg-neutral-800 p-6">
                <img loading='lazy' :src="productIcon( course )" alt="Product icon"
                     class="h-12 w-12 flex-none rounded-lg object-cover ring-1 ring-neutral-900/10"/>
                <div>
                  <div class="text-xs uppercase">{{ productName( course ) }}</div>
                  <div class="text-sm font-medium leading-6">{{ course.title }}</div>
                  <div class="text-muted text-[10px]">
                    {{ totalLessons( course ) }}
                  </div>
                </div>
              </div>
              <div class="p-2">
                <router-link :to="routeManager.courseRouteBySlug( course.slug )" class="btn btn-sm w-full">Start Course</router-link>
              </div>
            </li>
          </ul>
        </div>
      </PageLayout>
    </div>
  </transition>
</template>
